'use client';

import { useState, useEffect } from 'react';
import { RadarChart, Radar, PolarGrid, PolarAngleAxis, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LineChart, Line } from 'recharts';
import Icon from '@/components/ui/AppIcon';

interface ModelWeight {
  name: string;
  weight: number;
  confidence: number;
  contextFit: number;
  color: string;
}

interface MatchContext {
  label: string;
  value: string;
  icon: string;
}

const BASE_MODELS = [
  { name: 'Poisson v3', weight: 18, confidence: 68, contextFit: 72, color: '#3b82f6' },
  { name: 'Elo Advanced', weight: 22, confidence: 71, contextFit: 85, color: '#a855f7' },
  { name: 'xG Neural', weight: 31, confidence: 74, contextFit: 91, color: '#10b981' },
  { name: 'Momentum ML', weight: 14, confidence: 66, contextFit: 68, color: '#f59e0b' },
  { name: 'Fatigue Model', weight: 9, confidence: 63, contextFit: 74, color: '#ef4444' },
  { name: 'Market Signal', weight: 6, confidence: 61, contextFit: 62, color: '#06b6d4' },
];

const CONTEXT_OPTIONS = [
  { label: 'Back-to-Back', value: 'b2b', icon: 'CalendarDaysIcon' },
  { label: 'Playoff Race', value: 'playoff', icon: 'TrophyIcon' },
  { label: 'Home Ice', value: 'home', icon: 'HomeIcon' },
  { label: 'Goalie Confirmed', value: 'goalie', icon: 'ShieldCheckIcon' },
  { label: 'High Volatility', value: 'volatile', icon: 'BoltIcon' },
  { label: 'Rivalry Match', value: 'rivalry', icon: 'FireIcon' },
];

const BLEND_HISTORY = Array.from({ length: 20 }, (_, i) => ({
  game: `G${i + 1}`,
  ensemble: 72 + Math.sin(i * 0.4) * 5 + Math.random() * 3,
  best_single: 68 + Math.cos(i * 0.3) * 4 + Math.random() * 2,
  baseline: 63 + Math.random() * 2,
}));

export default function MetaModelClient() {
  const [selectedContexts, setSelectedContexts] = useState<string[]>(['b2b', 'goalie']);
  const [models, setModels] = useState<ModelWeight[]>(BASE_MODELS);
  const [blendMode, setBlendMode] = useState<'dynamic' | 'equal' | 'confidence'>('dynamic');
  const [ensembleScore, setEnsembleScore] = useState(76.3);

  useEffect(() => {
    // Recalculate weights based on context
    const contextMultipliers: Record<string, Record<string, number>> = {
      b2b: { 'Fatigue Model': 2.5, 'Momentum ML': 1.8, 'Poisson v3': 0.7 },
      playoff: { 'Elo Advanced': 2.0, 'xG Neural': 1.5, 'Market Signal': 1.8 },
      home: { 'Elo Advanced': 1.4, 'Poisson v3': 1.2 },
      goalie: { 'xG Neural': 2.2, 'Poisson v3': 1.6 },
      volatile: { 'Market Signal': 2.5, 'Momentum ML': 1.9 },
      rivalry: { 'Momentum ML': 2.0, 'Elo Advanced': 1.5 },
    };

    const newModels = BASE_MODELS.map(m => {
      let multiplier = 1;
      selectedContexts.forEach(ctx => {
        if (contextMultipliers[ctx]?.[m.name]) {
          multiplier *= contextMultipliers[ctx][m.name];
        }
      });
      return { ...m, weight: Math.min(Math.round(m.weight * multiplier), 50) };
    });

    // Normalize weights
    const total = newModels.reduce((s, m) => s + m.weight, 0);
    const normalized = newModels.map(m => ({ ...m, weight: Math.round((m.weight / total) * 100) }));
    setModels(normalized);
    setEnsembleScore(+(72 + selectedContexts.length * 1.2 + Math.random() * 2).toFixed(1));
  }, [selectedContexts, blendMode]);

  const toggleContext = (ctx: string) => {
    setSelectedContexts(prev => prev.includes(ctx) ? prev.filter(c => c !== ctx) : [...prev, ctx]);
  };

  const radarData = models.map(m => ({ metric: m.name.split(' ')[0], weight: m.weight, confidence: m.confidence }));

  return (
    <div className="min-h-screen bg-background text-foreground p-4 space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <Icon name="CircleStackIcon" size={20} className="text-purple-400" />
            <h1 className="text-xl font-bold text-foreground">Meta Model Orchestrator</h1>
            <span className="px-2 py-0.5 text-2xs font-mono bg-purple-400/10 text-purple-400 border border-purple-400/20 rounded">PHASE QUANT · ÉTAPE 2</span>
          </div>
          <p className="text-sm text-muted-foreground">Sélection dynamique du meilleur modèle selon le contexte match</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="text-right">
            <div className="text-2xs text-muted-foreground font-mono">ENSEMBLE ACCURACY</div>
            <div className="text-2xl font-bold text-green-400 font-mono">{ensembleScore}%</div>
          </div>
        </div>
      </div>

      {/* Context Selector */}
      <div className="bg-card border border-border rounded-lg p-4">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-semibold text-foreground">Contexte Match</h3>
          <div className="flex gap-1">
            {(['dynamic', 'equal', 'confidence'] as const).map(mode => (
              <button
                key={mode}
                onClick={() => setBlendMode(mode)}
                className={`px-3 py-1 text-xs rounded border transition-colors ${blendMode === mode ? 'bg-primary/20 border-primary text-primary' : 'border-border text-muted-foreground hover:border-primary/50'}`}
              >
                {mode === 'dynamic' ? 'Dynamique' : mode === 'equal' ? 'Égal' : 'Confiance'}
              </button>
            ))}
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          {CONTEXT_OPTIONS.map(ctx => (
            <button
              key={ctx.value}
              onClick={() => toggleContext(ctx.value)}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg border text-xs transition-all ${selectedContexts.includes(ctx.value) ? 'bg-primary/20 border-primary text-primary' : 'border-border text-muted-foreground hover:border-primary/50'}`}
            >
              <Icon name={ctx.icon as Parameters<typeof Icon>[0]['name']} size={12} />
              {ctx.label}
            </button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-3 gap-4">
        {/* Model Weights Bar */}
        <div className="col-span-2 bg-card border border-border rounded-lg p-4">
          <h3 className="text-sm font-semibold text-foreground mb-4">Poids des Modèles (Contexte Actuel)</h3>
          <div className="space-y-3">
            {models.sort((a, b) => b.weight - a.weight).map(model => (
              <div key={model.name} className="flex items-center gap-3">
                <span className="text-xs text-muted-foreground w-28 truncate">{model.name}</span>
                <div className="flex-1 h-6 bg-border/30 rounded overflow-hidden relative">
                  <div
                    className="h-full rounded transition-all duration-700 flex items-center justify-end pr-2"
                    style={{ width: `${model.weight}%`, backgroundColor: model.color + '40', borderRight: `2px solid ${model.color}` }}
                  >
                    <span className="text-2xs font-mono font-bold" style={{ color: model.color }}>{model.weight}%</span>
                  </div>
                </div>
                <div className="text-right w-16">
                  <div className="text-xs font-mono text-muted-foreground">Conf: {model.confidence}%</div>
                </div>
              </div>
            ))}
          </div>
          <div className="mt-4 p-3 bg-background rounded border border-border flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Icon name="SparklesIcon" size={14} className="text-green-400" />
              <span className="text-xs text-muted-foreground">Modèle dominant:</span>
              <span className="text-xs font-bold text-green-400">{models.sort((a, b) => b.weight - a.weight)[0]?.name}</span>
            </div>
            <div className="text-xs font-mono text-muted-foreground">
              Blend: <span className="text-primary font-bold">{blendMode.toUpperCase()}</span>
            </div>
          </div>
        </div>

        {/* Radar */}
        <div className="bg-card border border-border rounded-lg p-4">
          <h3 className="text-sm font-semibold text-foreground mb-3">Profil Ensemble</h3>
          <ResponsiveContainer width="100%" height={200}>
            <RadarChart data={radarData}>
              <PolarGrid stroke="#334155" />
              <PolarAngleAxis dataKey="metric" tick={{ fill: '#94a3b8', fontSize: 10 }} />
              <Radar dataKey="weight" stroke="#a855f7" fill="#a855f7" fillOpacity={0.2} strokeWidth={2} name="Poids" />
              <Radar dataKey="confidence" stroke="#10b981" fill="#10b981" fillOpacity={0.1} strokeWidth={1.5} name="Confiance" />
            </RadarChart>
          </ResponsiveContainer>
          <div className="grid grid-cols-2 gap-2 mt-2">
            <div className="p-2 bg-background rounded border border-border text-center">
              <div className="text-lg font-bold text-purple-400 font-mono">{models.length}</div>
              <div className="text-2xs text-muted-foreground">Modèles actifs</div>
            </div>
            <div className="p-2 bg-background rounded border border-border text-center">
              <div className="text-lg font-bold text-green-400 font-mono">{selectedContexts.length}</div>
              <div className="text-2xs text-muted-foreground">Contextes actifs</div>
            </div>
          </div>
        </div>
      </div>

      {/* Ensemble vs Single performance */}
      <div className="bg-card border border-border rounded-lg p-4">
        <h3 className="text-sm font-semibold text-foreground mb-3">Ensemble vs Meilleur Modèle Unique (20 derniers matchs)</h3>
        <ResponsiveContainer width="100%" height={200}>
          <LineChart data={BLEND_HISTORY}>
            <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" />
            <XAxis dataKey="game" tick={{ fill: '#64748b', fontSize: 10 }} />
            <YAxis tick={{ fill: '#64748b', fontSize: 10 }} domain={[58, 82]} />
            <Tooltip contentStyle={{ background: '#0f172a', border: '1px solid #1e293b', borderRadius: 6, fontSize: 11 }} />
            <Line type="monotone" dataKey="ensemble" stroke="#10b981" strokeWidth={2.5} dot={false} name="Ensemble %" />
            <Line type="monotone" dataKey="best_single" stroke="#3b82f6" strokeWidth={1.5} dot={false} name="Meilleur Unique %" strokeDasharray="4 2" />
            <Line type="monotone" dataKey="baseline" stroke="#475569" strokeWidth={1} dot={false} name="Baseline %" strokeDasharray="2 4" />
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}
